import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import * as api from '../services/api';
import { convertGoogleDriveUrlForDisplay, getPlaceholderImage } from '../utils/imageUtils';
import './OrderHistory.css';

export default function OrderHistory() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    loadOrders();
  }, [isAuthenticated, navigate]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const ordersData = await api.getUserOrders();
      setOrders(ordersData);
    } catch (error) {
      console.error('Error loading orders:', error);
      setError('Failed to load orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'pending': '#ff9800',
      'confirmed': '#2196f3',
      'processing': '#9c27b0',
      'shipped': '#3f51b5',
      'delivered': '#4caf50',
      'cancelled': '#f44336'
    };
    return statusColors[status?.toLowerCase()] || '#666';
  };

  if (loading) {
    return (
      <div className="order-history-container">
        <div className="loading-state">
          <p>Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="order-history-container">
      <div className="order-history-header">
        <button onClick={() => navigate('/')} className="back-button">
          ← Back to Home
        </button>
        <h1>Order History</h1>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {orders.length === 0 ? (
        <div className="empty-orders">
          <div className="empty-icon">📦</div>
          <h2>No orders yet</h2>
          <p>You haven't placed any orders. Start shopping to see your orders here!</p>
          <button onClick={() => navigate('/')} className="btn primary">
            Start Shopping
          </button>
        </div>
      ) : (
        <div className="orders-list">
          {orders.map((order) => (
            <div key={order._id} className="order-card">
              <div className="order-header">
                <div className="order-info">
                  <div className="order-id">Order #{order._id.slice(-8).toUpperCase()}</div>
                  <div className="order-date">{formatDate(order.createdAt)}</div>
                </div>
                <div 
                  className="order-status"
                  style={{ color: getStatusColor(order.status) }}
                >
                  {order.status || 'Pending'}
                </div>
              </div>

              <div className="order-items">
                {order.items?.map((item, index) => {
                  // Get product image - check if product is populated or if images are in item
                  const productImage = item.product?.images?.[0] || 
                                     item.images?.[0] || 
                                     item.image || 
                                     null;
                  const imageUrl = productImage ? convertGoogleDriveUrlForDisplay(productImage) : getPlaceholderImage(60, 60, 'No Image');
                  
                  return (
                    <div key={index} className="order-item">
                      <div className="item-details">
                        <div className="item-name-wrapper">
                          <img 
                            src={imageUrl}
                            alt={item.title || item.name || 'Product'}
                            className="order-item-image"
                            onError={(e) => {
                              // Prevent infinite retry loops
                              if (e.target.dataset.retryCount) {
                                e.target.src = getPlaceholderImage(60, 60, 'No Image');
                                e.target.onerror = null;
                                return;
                              }
                              e.target.dataset.retryCount = '1';
                              
                              const originalSrc = e.target.src;
                              // Skip problematic URLs
                              if (originalSrc.includes('lh3.googleusercontent.com') || 
                                  originalSrc.includes('drive.google.com/uc?export=download')) {
                                e.target.src = getPlaceholderImage(60, 60, 'No Image');
                                e.target.onerror = null;
                                return;
                              }
                              
                              e.target.src = getPlaceholderImage(60, 60, 'No Image');
                              e.target.onerror = null;
                            }}
                          />
                          <div className="item-name">{item.title || item.name || 'Product'}</div>
                        </div>
                        {item.sku && (
                          <div className="item-variant">Variant: {item.sku}</div>
                        )}
                        <div className="item-quantity">Qty: {item.qty || item.quantity}</div>
                      </div>
                      <div className="item-price">₹{item.price * (item.qty || item.quantity)}</div>
                    </div>
                  );
                })}
              </div>

              <div className="order-summary">
                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>₹{order.subtotal || 0}</span>
                </div>
                {order.shipping > 0 && (
                  <div className="summary-row">
                    <span>Shipping</span>
                    <span>₹{order.shipping}</span>
                  </div>
                )}
                <div className="summary-row total">
                  <span>Total</span>
                  <span>₹{order.total || 0}</span>
                </div>
              </div>

              {order.deliveryAddress && (
                <div className="order-address">
                  <strong>Delivery Address:</strong>
                  <div className="address-text">
                    {order.deliveryAddress.street && `${order.deliveryAddress.street}, `}
                    {order.deliveryAddress.city && `${order.deliveryAddress.city}, `}
                    {order.deliveryAddress.state && `${order.deliveryAddress.state} - `}
                    {order.deliveryAddress.pincode}
                  </div>
                </div>
              )}

              <div className="order-actions">
                <button
                  className="btn-secondary view-details-btn"
                  onClick={() => navigate(`/orders/${order._id}`)}
                >
                  View Details
                </button>
              </div>

              {order.paymentMethod && (
                <div className="order-payment">
                  <strong>Payment:</strong> {order.paymentMethod}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

